/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import React, { useCallback, useMemo } from 'react';

import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import CheckIcon from '@atlaskit/icon/core/check-mark';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, Inline, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import Tooltip from '@atlaskit/tooltip';

import { CONSENT_DISPLAYED_TEXT_ITEMS } from '../../../services/consent-hub-service/constants';
import type { ConsentDisplayedTextMap } from '../../../services/consent-hub-service/types';
import type { PreferenceCategory } from '../../../types';
import { i18n } from '../messages';

const boxStylesBlueWithMinHeight = xcss({
	padding: 'space.200',
	backgroundColor: 'color.background.information',
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderColor: 'color.border',
	overflowY: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
});

const boxStylesPurpleWithMinHeight = xcss({
	padding: 'space.200',
	backgroundColor: 'color.background.discovery',
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderColor: 'color.border',
	overflowY: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
});

const noticeContainerStyles = xcss({
	flexDirection: 'column',
});

const experimentContainerStyles = xcss({
	flexDirection: 'row',
	alignItems: 'center',
	gap: 'space.200',
});

const inlineWrapperStylesStaticHeight = xcss({
	// Small & Medium viewport defaults
	gap: 'space.100',
	flexDirection: 'column',
	alignItems: 'flex-start',
	paddingTop: 'space.200',
	paddingBottom: 'space.200',

	[media.above.md]: {
		gap: 'space.300',
		flexDirection: 'row',
		alignItems: 'center',
	},
});

const buttonContainerStylesStaticHeight = xcss({
	minWidth: 'fit-content',
	display: 'flex',
	alignItems: 'center',

	flexDirection: 'row-reverse',
	flexWrap: 'wrap-reverse',
	justifyContent: 'start',

	[media.above.md]: {
		marginLeft: 'auto',
		flexWrap: 'nowrap',
		flexDirection: 'row',
	},
});

const truncatedTextStyles = xcss({
	overflow: 'hidden',
	display: '-webkit-box',
	WebkitBoxOrient: 'vertical',
	WebkitLineClamp: 1,
	flex: '0 1 auto',
});

const linkNoTruncateStyles = xcss({
	flex: '0 0 auto',
	whiteSpace: 'nowrap',
});

const smallMediumScreenContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	gap: 'space.050',
	alignItems: 'baseline',

	[media.above.md]: {
		display: 'none',
	},
});

const largeScreenContainerStyles = xcss({
	display: 'none',

	[media.above.md]: {
		display: 'block',
	},
});

const noop = () => {};

interface PresentationalConsentBannerProps {
	onClickOnlyNecessary?: (displayedTextMap: ConsentDisplayedTextMap) => void;
	onClickAcceptAll?: (displayedTextMap: ConsentDisplayedTextMap) => void;
	onClickPreferences?: () => void;
	onClickTrackingNoticeLink?: () => void;
	minWidth?: number;
	newMinHeight?: number;
}

const getDisplayedTextMap = (intl: IntlShape): ConsentDisplayedTextMap => {
	// We'll use this mapping to send the `displayedText` for each checkbox when saving to ConsentHub
	const displayedTextMap = CONSENT_DISPLAYED_TEXT_ITEMS.reduce((translatedTextMap, category) => {
		const linkToCookiesTrackingNotice = intl.formatMessage(i18n.cookiesTrackingNotice);
		const bannerDescription = intl.formatMessage(i18n.useCookiesDescription, {
			linkToCookiesTrackingNotice,
		});
		// Set this for all items (including items with checkboxes in the UI), we'll override as needed below
		// for things like strictlyNecessary/acceptAll
		translatedTextMap[category] = bannerDescription;
		return translatedTextMap;
	}, {} as ConsentDisplayedTextMap);

	return displayedTextMap;
};

const updateDisplayedTextMap = (
	intl: IntlShape,
	displayTextMap: ConsentDisplayedTextMap,
	isOnlyNecessary?: boolean,
) => {
	const updatedDisplayedTextMap: ConsentDisplayedTextMap = {
		...displayTextMap,
	};

	// Use description as message to translate
	const onlyNecessaryContent = intl.formatMessage(i18n.onlyNecessaryBtn);
	const acceptAllContent = intl.formatMessage(i18n.acceptAllBtn);

	// For additional auditing content, append the description related to the button when pressed
	Object.keys(updatedDisplayedTextMap).forEach((key) => {
		const preferenceCategory = key as PreferenceCategory;
		const additionalContent = isOnlyNecessary ? onlyNecessaryContent : acceptAllContent;
		const textContent = updatedDisplayedTextMap[preferenceCategory];
		updatedDisplayedTextMap[preferenceCategory] = `${additionalContent} — ${textContent}`;
	});

	return updatedDisplayedTextMap;
};
/**
 * Component containing the UI aspects of the consent banner.
 *
 * Conditional rendering, analytics, feature flag evaluation, and most other
 * logic are located in the parent component ConsentBanner
 */
export const PresentationalConsentBanner = ({
	onClickOnlyNecessary = noop,
	onClickAcceptAll = noop,
	onClickPreferences = noop,
	onClickTrackingNoticeLink = noop,
	minWidth = 400,
	newMinHeight = 105,
}: PresentationalConsentBannerProps): React.JSX.Element => {
	const isCookiesBannerExperimentEnabled = FeatureGates.getExperimentValue(
		'cookies_banner_redesign_experiment',
		'isEnabled',
		false,
	);
	const intl = useIntl();

	const displayedTextMap = useMemo(() => {
		return getDisplayedTextMap(intl);
	}, [intl]);

	const handleOnlyNecessaryClick = useCallback(() => {
		const displayedTextOnlyNecessaryMap = updateDisplayedTextMap(intl, displayedTextMap, true);
		onClickOnlyNecessary(displayedTextOnlyNecessaryMap);
	}, [onClickOnlyNecessary, intl, displayedTextMap]);

	const handleAcceptAllClick = useCallback(() => {
		const displayedTextAcceptAllMap = updateDisplayedTextMap(intl, displayedTextMap);
		onClickAcceptAll(displayedTextAcceptAllMap);
	}, [onClickAcceptAll, intl, displayedTextMap]);
	return (
		<Box
			role="dialog"
			aria-labelledby="cookiesTrackingNoticeLink"
			xcss={
				isCookiesBannerExperimentEnabled ? boxStylesPurpleWithMinHeight : boxStylesBlueWithMinHeight
			}
			style={{
				minWidth: `${minWidth}px`,
				// If you are updating the minHeight, please also update the MIN_BANNER_HEIGHT in confluence/next/packages/cookies-consent-banner/src/useCookiesConsentBanner.ts
				minHeight: `${newMinHeight}px`,
				height: `${newMinHeight}px`,
			}}
		>
			<Inline xcss={inlineWrapperStylesStaticHeight}>
				<Inline
					xcss={
						isCookiesBannerExperimentEnabled ? experimentContainerStyles : noticeContainerStyles
					}
				>
					<>
						<Inline xcss={smallMediumScreenContainerStyles}>
							<Tooltip
								content={<FormattedMessage {...i18n.useCookiesDescriptionNoLink} />}
								position="top"
							>
								<Inline xcss={truncatedTextStyles}>
									<FormattedMessage {...i18n.useCookiesDescriptionNoLink} tagName="span" />
								</Inline>
							</Tooltip>
							<Anchor
								id="cookiesTrackingNoticeLink"
								href="https://www.atlassian.com/legal/cookies"
								target="_blank"
								onClick={onClickTrackingNoticeLink}
								xcss={linkNoTruncateStyles}
							>
								<FormattedMessage {...i18n.cookiesTrackingNotice} />
							</Anchor>
						</Inline>
						{/* Larger screens: full text with inline link, no truncation */}
						<Box xcss={largeScreenContainerStyles}>
							<FormattedMessage
								{...i18n.useCookiesDescription}
								tagName="span"
								values={{
									linkToCookiesTrackingNotice: (
										<Anchor
											id="cookiesTrackingNoticeLink"
											href="https://www.atlassian.com/legal/cookies"
											target="_blank"
											onClick={onClickTrackingNoticeLink}
										>
											<FormattedMessage {...i18n.cookiesTrackingNotice} />
										</Anchor>
									),
								}}
							/>
						</Box>
					</>
				</Inline>
				<Inline space="space.100" xcss={buttonContainerStylesStaticHeight}>
					<Button testId="preferences-button" appearance="subtle" onClick={onClickPreferences}>
						<FormattedMessage {...i18n.preferencesBtn} />
					</Button>
					<Button testId="only-necessary-button" onClick={handleOnlyNecessaryClick}>
						<FormattedMessage {...i18n.onlyNecessaryBtn} />
					</Button>
					<Button
						testId="accept-all-button"
						iconBefore={CheckIcon}
						onClick={handleAcceptAllClick}
					>
						<FormattedMessage {...i18n.acceptAllBtn} />
					</Button>
				</Inline>
			</Inline>
		</Box>
	);
};
